import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "@phosphor-icons/react";
import { FAQS } from "@/lib/faqs";

const TONES = {
  dark: {
    section: "",
    eyebrow: "text-zinc-400",
    eyebrowLine: "bg-zinc-600",
    title: "text-white",
    intro: "text-zinc-300",
    ask: "text-white decoration-zinc-500 hover:decoration-white",
    border: "border-white/[0.07]",
    q: "text-white",
    plus: "border-white/10 text-zinc-400 group-hover:border-white/25",
    plusOpen: "text-white",
    a: "text-zinc-300",
    more: "text-zinc-400",
    moreLink: "text-zinc-300 hover:text-white decoration-zinc-700",
  },
  light: {
    section: "tone-light bg-white",
    eyebrow: "text-brand-700",
    eyebrowLine: "bg-brand-700",
    title: "text-zinc-900",
    intro: "text-zinc-600",
    ask: "text-zinc-900 decoration-zinc-400 hover:decoration-brand-700",
    border: "border-zinc-200",
    q: "text-zinc-900",
    plus: "border-zinc-300 text-zinc-600 group-hover:border-zinc-900",
    plusOpen: "bg-zinc-900 border-zinc-900 !text-white",
    a: "text-zinc-600",
    more: "text-zinc-600",
    moreLink: "text-zinc-700 hover:text-brand-700 decoration-zinc-300",
  },
};

function FaqItem({ q, a, index, open, onToggle, t }) {
  const id = `faq-${index}`;
  return (
    <div className={`border-b ${t.border}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={`${id}-button`}
        data-testid={`faq-question-${index}`}
        className="w-full flex items-start justify-between gap-6 py-5 sm:py-6 text-left group"
      >
        <span className={`font-display text-[17px] sm:text-[19px] tracking-tight leading-snug ${t.q}`}>
          {q}
        </span>
        <span
          className={`mt-1 inline-flex flex-shrink-0 items-center justify-center w-7 h-7 rounded-full border transition-[transform,background-color,color] duration-300 ${t.plus} ${
            open ? `rotate-45 ${t.plusOpen}` : ""
          }`}
        >
          <Plus size={13} weight="bold" />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={`${id}-button`}
            data-testid={`faq-answer-${index}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className={`pb-6 pr-10 text-[14.5px] sm:text-[15px] leading-relaxed max-w-2xl ${t.a}`}>
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ({ items = FAQS, title = "Questions, answered.", eyebrow = "FAQ", showMore = false, askHref = "#contact", intro, tone = "dark" }) {
  const [openIndex, setOpenIndex] = useState(0);
  const t = TONES[tone];
  return (
    <section
      id="faq"
      data-tone={tone}
      data-testid="faq-section"
      className={`relative py-16 sm:py-24 md:py-32 scroll-mt-16 md:scroll-mt-20 ${t.section}`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <div className={`text-[11.5px] tracking-[0.22em] uppercase flex items-center gap-3 ${t.eyebrow}`}>
              <span className={`block w-8 h-px ${t.eyebrowLine}`} />
              {eyebrow}
            </div>
            <h2 className={`mt-5 font-display font-bold text-[34px] sm:text-4xl lg:text-[48px] leading-[1.05] tracking-tight ${t.title}`}>
              {title}
            </h2>
            <p className={`mt-5 text-[15px] leading-relaxed max-w-sm ${t.intro}`}>
              {intro ||
                "Straight answers on pricing, what's included, and how our services fit together."}{" "}
              Anything else —{" "}
              <a href={askHref} className={`underline underline-offset-4 ${t.ask}`}>
                just ask
              </a>
              .
            </p>
          </div>
          <div className={`lg:col-span-8 border-t ${t.border}`}>
            {items.map((item, i) => (
              <FaqItem
                key={item.q}
                {...item}
                index={i}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                t={t}
              />
            ))}
            {showMore && (
              <div
                data-testid="faq-more-links"
                className={`mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13.5px] ${t.more}`}
              >
                <span>More questions:</span>
                <Link to="/local-visibility#faq" className={`underline underline-offset-4 transition-colors ${t.moreLink}`}>
                  Local Visibility
                </Link>
                <Link to="/website-review#faq" className={`underline underline-offset-4 transition-colors ${t.moreLink}`}>
                  Improving an existing site
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
